import React from "react";
import { Flex, type BoxProps, SimpleGrid, Text, type TextProps, Box } from "@chakra-ui/react";
import Image from "next/image";
import Link from "next/link";
import { type Customer } from "lib/sanity";
import { type Theme, type ThemeBgColor } from "contexts/color-theme";
import Balancer from "react-wrap-balancer";
import { Section } from "components/section";
import { Button } from "ui/button";
import { getCustomerLogoUrl, getCustomerLogoStyles, mobileOnlyDisplay, desktopOnlyDisplay } from "utils";
import { trackCustomerLogosClicked } from "components/event-tracking";

const defaultMobileSlugs = new Set([
  "spotify",
  "wmg",
  "petsmart",
  "weight-watchers",
  "ramp",
  "calendly",
  "grammarly",
  "gitlab",
]);

type StackedLogosProps = {
  hasLink?: boolean; // If true, the logos will be wrapped in a link to the /customers page with a hover effect.
  title?: string;
  titleMaxW?: TextProps["maxW"];
  customerRows: Customer[][];
  mobileCustomers?: Customer[];
  theme?: Theme;
  themeBgColor?: ThemeBgColor;
} & BoxProps;

export function StackedLogos({
  hasLink = true,
  title = "The world’s most innovative companies choose Hightouch as their Customer Data and AI Decisioning Platform",
  titleMaxW = "4xl",
  customerRows,
  mobileCustomers,
  theme = "light",
  themeBgColor = "white",
  ...props
}: StackedLogosProps) {
  const allCustomers: Customer[] = [];
  for (const row of customerRows) for (const customer of row) allCustomers.push(customer);
  if (allCustomers.length === 0) {
    return null;
  }

  const mobileLogos = mobileCustomers ?? allCustomers.filter((customer) => defaultMobileSlugs.has(customer.slug));

  const linkProps = hasLink ? { "aria-label": "Read case studies", className: "group", href: "/customers" } : {};

  const handleLogoClick = () => {
    if (hasLink) {
      trackCustomerLogosClicked("stacked-logos");
    }
  };

  return (
    <Section theme={theme} themeBgColor={themeBgColor} {...props}>
      {title && (
        <Text
          color={`brand.text.${theme}`}
          textStyle={{ base: "headline3", md: "headline2" }}
          textAlign="center"
          maxW={titleMaxW}
          mx="auto"
          mb={{ base: "bigger", md: "biggest" }}
        >
          <Balancer>{title}</Balancer>
        </Text>
      )}
      <SimpleGrid
        as={hasLink ? Link : "div"}
        display={mobileOnlyDisplay("grid")}
        columns={4}
        alignItems="center"
        spacing="big"
        maxW={{ base: "100%", sm: "90%" }}
        mx="auto"
        _focus={{ outline: "none !important" }}
        _focusVisible={{
          outline: "none !important",
          boxShadow: "outline",
        }}
        onClick={handleLogoClick}
        {...linkProps}
      >
        {mobileLogos.filter(Boolean).map((customer: Customer) => (
          <Flex
            key={customer.slug}
            pos="relative"
            align="center"
            justify="center"
            maxW="100%"
            w="100%"
            {...getCustomerLogoStyles(theme)}
          >
            <Image
              src={`${getCustomerLogoUrl(customer, theme)}?w=300`}
              alt={`${customer.name}.`}
              sizes="100%"
              width={5}
              height={2}
              style={{
                objectFit: "contain",
                objectPosition: "center",
                width: "100%",
                height: "unset",
                minWidth: "60px",
                maxWidth: "100px",
                maxHeight: "36px",
              }}
            />
          </Flex>
        ))}
      </SimpleGrid>

      <Box
        as={hasLink ? Link : "div"}
        display={desktopOnlyDisplay("grid")}
        pos="relative"
        _focus={{ outline: "none !important" }}
        _focusVisible={{
          outline: "none !important",
        }}
        onClick={handleLogoClick}
        {...linkProps}
      >
        <SimpleGrid
          columns={{ base: 4, lg: 8 }}
          alignItems="center"
          spacing="big"
          w="100%"
          maxW={{ base: "3xl", lg: "100%" }}
          mx="auto"
          transition="all 0.15s ease-in-out"
          _groupHover={{ filter: "blur(4px)", opacity: 0.7 }}
          _groupFocusVisible={{ filter: "blur(4px)", opacity: 0.7 }}
        >
          {allCustomers.filter(Boolean).map((customer: Customer) => (
            <Flex
              key={customer.slug}
              pos="relative"
              align="center"
              justify="center"
              w="100%"
              maxW="100%"
              {...getCustomerLogoStyles(theme)}
            >
              <Image
                src={`${getCustomerLogoUrl(customer, theme)}?w=300`}
                alt={`${customer.name}.`}
                sizes="100%"
                width={5}
                height={2}
                style={{
                  objectFit: "contain",
                  objectPosition: "center",
                  width: "100%",
                  height: "unset",
                  minWidth: "60px",
                  maxWidth: "100px",
                  maxHeight: "36px",
                }}
              />
            </Flex>
          ))}
        </SimpleGrid>

        {hasLink && (
          <Flex
            align="center"
            justify="center"
            pos="absolute"
            inset={0}
            opacity={0}
            transform="scale(0.9)"
            transition="all 0.15s ease-in-out"
            _groupHover={{ opacity: 1, transform: "scale(1)" }}
            _groupFocusVisible={{ opacity: 1, transform: "scale(1)" }}
          >
            <Button
              hasRightChevron
              as="div"
              href="/customers"
              variant="secondary"
              size="sm"
              // Override default border color so it always matches the hover state.
              borderColor="brand.textSecondary.light"
              _groupFocusVisible={{ boxShadow: "outline" }}
            >
              Read case studies
            </Button>
          </Flex>
        )}
      </Box>
    </Section>
  );
}
